import { type FC, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

// biome-ignore lint/style/useComponentExportOnlyModules: constant shared with GatedPostCard
export const DEFAULT_COLLAPSED_MAX_HEIGHT_CLASS = 'max-h-48';

export interface ReadMoreClampProps {
  children: ReactNode;
  collapsedMaxHeightClassName?: string;
  className?: string;
}

export const ReadMoreClamp: FC<ReadMoreClampProps> = ({
  children,
  collapsedMaxHeightClassName = DEFAULT_COLLAPSED_MAX_HEIGHT_CLASS,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || expanded) return;

    const updateOverflow = () => setIsOverflowing(element.scrollHeight > element.clientHeight);
    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded]);

  const handleToggle = () => {
    const willCollapse = expanded;
    setExpanded((value) => !value);
    if (willCollapse) {
      requestAnimationFrame(() => {
        buttonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  };

  const showFade = isOverflowing && !expanded;

  return (
    <div className={className}>
      <div
        ref={contentRef}
        data-testid="read-more-clamp-content"
        className={cn('relative', { [collapsedMaxHeightClassName]: !expanded, 'overflow-hidden': !expanded })}
      >
        {children}
        {showFade && (
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white via-white/70 to-transparent pointer-events-none z-10" />
        )}
      </div>
      {isOverflowing && (
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={expanded}
          onClick={handleToggle}
          className="-mt-6 relative z-10 text-[15px] font-medium text-black/50 hover:text-black transition-colors"
        >
          {expanded ? t('community.post.read_less') : t('community.post.read_more')}
        </button>
      )}
    </div>
  );
};
